/**
 * The report history list (HS-1602): URL search, filter fields, and the one mapping onto the run
 * history query (HS-1594 `GET /api/reports/runs`).
 */
import { DEFAULT_PAGE, sortSpecSchema } from '@smartsash/data-screen';
import type { FilterCondition } from '@smartsash/ui';
import { z } from 'zod';

import { REPORTS_MAX_PAGE_SIZE, RUN_SOURCES, RUN_STATUSES } from '../reports.constants';
import type { ReportRunListItemDto, ReportRunsParams, RunSort } from '../reports.types';
import { isOneOf } from '../reportsGuards';
import { labNameOf } from '../reportsModel';

import { adminListShape, LIST_PAGE_SIZE } from '@/router/listSearch';

/** Filter-builder field ids. */
export const RUNS_TEMPLATE_FIELD = 'template';
export const RUNS_STATUS_FIELD = 'status';
export const RUNS_SOURCE_FIELD = 'source';
export const RUNS_INSTITUTION_FIELD = 'institution';
export const RUNS_LAB_FIELD = 'laboratory';
export const RUNS_CREATED_FIELD = 'createdAt';

/** Sortable column ids and the contract's sort field each one asks for. */
const SORT_FIELDS = {
  report: 'reportType',
  status: 'status',
  created: 'createdAt',
} as const;

export const reportsSearchSchema = z.object({
  page: z.coerce.number().int().positive().catch(DEFAULT_PAGE),
  pageSize: z.coerce
    .number()
    .int()
    .positive()
    .catch(LIST_PAGE_SIZE)
    .transform((size) => Math.min(size, REPORTS_MAX_PAGE_SIZE)),
  sort: sortSpecSchema.optional().catch(undefined),
  q: z.string().trim().optional().catch(undefined),
  filters: adminListShape.filters,
});

export type ReportsSearch = z.infer<typeof reportsSearchSchema>;

/**
 * Every value an "Is any of" condition carries (QB6): the builder's `enum` field writes one `eq`
 * condition holding each ticked value, and the contract takes the parameter repeated (ORed).
 */
function equalsValues(conditions: readonly FilterCondition[], field: string): string[] {
  const values = conditions.find((item) => item.field === field && item.operator === 'eq')?.values;
  return (values ?? []).filter((value) => value !== '');
}

function equalsValue(conditions: readonly FilterCondition[], field: string): string | undefined {
  const value = conditions.find((item) => item.field === field && item.operator === 'eq')
    ?.values[0];
  return value === undefined || value === '' ? undefined : value;
}

function sortOf(search: ReportsSearch): RunSort | undefined {
  const sort = search.sort;
  if (sort === undefined || !Object.hasOwn(SORT_FIELDS, sort.field)) return undefined;
  const field = SORT_FIELDS[sort.field as keyof typeof SORT_FIELDS];
  return sort.dir === 'desc' ? `-${field}` : field;
}

/** A search term or an applied filter — the list is empty because of what was asked, not "yet". */
export function isNarrowedSearch(search: Pick<ReportsSearch, 'q' | 'filters'>): boolean {
  return (search.q !== undefined && search.q !== '') || (search.filters ?? []).length > 0;
}

/** URL search → the history query. The screen and the filter's cache read both call it. */
export function reportsQueryParams(search: ReportsSearch): ReportRunsParams {
  const conditions = search.filters ?? [];
  const params: ReportRunsParams = {
    page: search.page,
    pageSize: Math.min(search.pageSize, REPORTS_MAX_PAGE_SIZE),
  };
  if (search.q !== undefined && search.q !== '') params.search = search.q;
  const sort = sortOf(search);
  if (sort !== undefined) params.sort = sort;

  const templates = equalsValues(conditions, RUNS_TEMPLATE_FIELD);
  if (templates.length > 0) params.reportType = templates;

  // A hand-edited URL may carry a status the contract does not know; it is dropped, not sent.
  const statuses = equalsValues(conditions, RUNS_STATUS_FIELD).filter((status) =>
    isOneOf(RUN_STATUSES, status),
  );
  if (statuses.length > 0) params.status = statuses;

  const source = equalsValue(conditions, RUNS_SOURCE_FIELD);
  if (source !== undefined && isOneOf(RUN_SOURCES, source)) params.source = source;

  const institutionId = equalsValue(conditions, RUNS_INSTITUTION_FIELD);
  if (institutionId !== undefined) params.institutionId = institutionId;

  const labId = equalsValue(conditions, RUNS_LAB_FIELD);
  if (labId !== undefined) params.labId = labId;

  const created = conditions.find(
    (item) => item.field === RUNS_CREATED_FIELD && item.operator === 'between',
  );
  const [from = '', to = ''] = created?.values ?? [];
  if (from !== '') params.createdFrom = from;
  if (to !== '') params.createdTo = to;

  return params;
}

/** The text each column holds for a row — the table's `value` readers (search, sort, export). */
export const reportColumnText = {
  report: (row: ReportRunListItemDto) => `${row.templateKey} ${String(row.number)}`,
  institution: (row: ReportRunListItemDto) => row.institution.name,
  laboratory: (row: ReportRunListItemDto) => labNameOf(row.scope) ?? '',
  period: (row: ReportRunListItemDto) => `${row.period.from} ${row.period.to}`,
  source: (row: ReportRunListItemDto) => row.source,
  status: (row: ReportRunListItemDto) => row.status,
  format: (row: ReportRunListItemDto) => [row.formats.pdf, row.formats.csv].join(' '),
  created: (row: ReportRunListItemDto) => row.createdAt,
} as const;

export type ReportColumnId = keyof typeof reportColumnText;
